import React from 'react';
import {Image, Text, View} from '@react-pdf/renderer';
import styles, {
    getClosestAndLowestIndex,
    getDepotAddress,
    getRadiusFromFilterSummary
} from '../../Foundations/Utils/PDFUtils';
import {IResultSummary} from '../../Foundations/Types/IResultsData';
import {IGenericNumberObject, IGenericStringArrayObject} from '../../Foundations/Types/IGenericObject';
import {ISearchFilterSet} from '../../Foundations/Types/ISearchFilter';
import {IDepotInfo, IDepotSummary} from '../../Foundations/Types/IDepot';
import {getSupplierName} from '../../Foundations/Utils/SupplierUtils';
import {ActiveReportFilters} from '../../Foundations/Enum/ActiveReportFilters';

interface IProps {
    mapSnapShot: string;
    resultSummary: IResultSummary;
    totalAvailableCars: number;
    filterSummary: IGenericStringArrayObject | null;
    availableFilters: ISearchFilterSet | null;
    suppliersAtArea: string[];
    depotsWithNoResults: IDepotSummary[];
    activeReportFilters: ActiveReportFilters[];
    depotsAtArea: IDepotInfo[];
}

const DepotSummary: React.FC<IProps> = (props) => {
    const radius: string = getRadiusFromFilterSummary(props.filterSummary, props.availableFilters);
    const lowestAndClosestIndex: IGenericNumberObject = getClosestAndLowestIndex(props.resultSummary);
    const hasRadiusFilter: boolean = props.activeReportFilters.indexOf(ActiveReportFilters.RADIUS) > -1;
    const getBackgroundColor = (index, item: IDepotSummary): string => {
        if (
            props.resultSummary.lowestPerDay !== null &&
            item.lowestPerDay !== null &&
            item.lowestPerDay.id === props.resultSummary.lowestPerDay.id
        ) {
            return '#0A68FF';
        }

        if (index === 0) {
            return '#EA913A'
        }

        return '#233B79';
    }

    const totalDepots: number = props.activeReportFilters.indexOf(ActiveReportFilters.DEPOTS) < 0
        ? props.resultSummary.depotSummary.length + props.depotsWithNoResults.length
        : props.resultSummary.depotSummary.length;

    return (
        <View>
            <View style={
                [
                    {...styles.row, ...styles.mb},
                    {paddingTop: 10, paddingBottom: 20, paddingLeft: 20, paddingRight: 20},
                    {backgroundColor: '#D8E3F2'}
                ]
            }
            >
                {
                    (
                        props.activeReportFilters.indexOf(ActiveReportFilters.SUPPLIERS) < 0 &&
                        props.activeReportFilters.indexOf(ActiveReportFilters.DEPOTS) < 0
                    ) &&
                    <View style={{...styles.depotSummaryCol}}>
                        <Text style={{...styles.wFull, ...styles.fontXs, ...styles.fontBold}}>
                            SUPPLIERS
                        </Text>
                        <Text style={{...styles.fontBold, ...styles.fontXl}}>
                            {Object.keys(props.resultSummary.suppliersWithResults).length}
                        </Text>
                    </View>
                }
                {
                    (
                        props.activeReportFilters.indexOf(ActiveReportFilters.DEPOTS) === -1 &&
                        props.activeReportFilters.indexOf(ActiveReportFilters.SUPPLIERS) === -1
                    ) &&
                    <View style={{...styles.depotSummaryCol}}>
                        <Text style={{...styles.wFull, ...styles.fontXs, ...styles.fontBold}}>
                            DEPOTS
                        </Text>
                        <Text style={{...styles.fontBold, ...styles.fontXl}}>
                            {Object.keys(props.resultSummary.depotsWithResults).length}
                        </Text>
                    </View>
                }
                {
                    props.activeReportFilters.indexOf(ActiveReportFilters.CAR_MODEL) < 0 &&
                    <View style={{...styles.depotSummaryCol}}>
                        <Text style={{...styles.wFull, ...styles.fontXs, ...styles.fontBold}}>
                            MODELS AVAILABLE
                        </Text>
                        <Text style={{...styles.fontBold, ...styles.fontXl}}>
                            {Object.keys(props.resultSummary.models).length}
                        </Text>
                    </View>
                }
                <View style={{...styles.depotSummaryCol}}>
                    <Text style={{...styles.wFull, ...styles.fontXs, ...styles.fontBold}}>
                        CARS AVAILABLE
                    </Text>
                    <Text style={{...styles.fontBold, ...styles.fontXl}}>
                        {props.totalAvailableCars}
                    </Text>
                </View>
                {
                    props.activeReportFilters.indexOf(ActiveReportFilters.DEPOTS) === -1 &&
                    <View style={{...styles.depotSummaryCol}}>
                        <Text style={{...styles.wFull, ...styles.fontXs, ...styles.fontBold}}>
                            { hasRadiusFilter ? 'SELECTED RADIUS' : 'RADIUS' }
                        </Text>
                        <Text style={{...styles.fontBold, ...styles.fontXl}}>
                            {radius}
                        </Text>
                    </View>
                }
            </View>
            {
                props.mapSnapShot !== '' &&
                <View style={{...styles.wFull, ...styles.mb}}>
                    <Image style={{ maxWidth: '100%'}}
                           src={props.mapSnapShot} allowDangerousPaths
                    />
                </View>
            }
            {
                props.resultSummary.lowestPerDay !== null &&
                props.resultSummary.closest !== null &&
                (
                    <View style={{...styles.mb, ...styles.reportHeader}}>
                        <View style={{...styles.row}}>
                            <View style={{...styles.wHalf, ...styles.fontXs}}>
                                <View style={{...styles.row}}>
                                    {
                                        typeof lowestAndClosestIndex.lowest !== 'undefined' &&
                                        <View style={{...styles.depotSummaryPinIconContainer}}>
                                            <Image allowDangerousPaths
                                                   style={{ maxWidth: 20}}
                                                   src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB4AAAAmCAYAAADTGStiAAAAAXNSR0IArs4c6QAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAHqADAAQAAAABAAAAJgAAAAB42OZ4AAADM0lEQVRYCbVXTUhUURQ+55riCO7SWrRoERGli1aB1KpFROUiC2sR/YA1QUEUBAWhRUGr2tQ0jgsVa1NBC/tBiCIQioIiiIjQoloE/VBRvjeT+k7ffePT63PezOs934WZe+75+b77e+59TGFKWhpriTYoovVwbxKhBmL8iuULqs9C9JKFHtoFGqI+/jllC6w40AJDdVpWLxDqhNjKDKoQBR2YgOPVSUXnChkeCQopDbZP6lPV1AOy9qDAMHpHKJuvo6N0kW2//xzimv2yokrRIAzL/M5R2liW9+xQu9XDz8z4WcQ1HbIKpI8x0nrTKa4M8jHM/7rxLL/wsKaJa9OyVAk9wUou8ozzWgt9cxStyV/hdxoXG1UXYQiDiZFqCqaF2PUDWtTFJU4doEOQm1xNgn+Y3hbM7B5N4RJDcSZBvlnQ4DqlFZxKSxsaN2dZE27grK/VI451ViP1UWi7woKvjBQcJ4ipWWG3NcbBiBQrtFxPtZfsI2FECUKCWuLu6ijBcWMUCVW8wuKS+OORQkeVML31GxJvMz3XU/00cSIfAW6rR8ohuu3TJ9pE8nCscbquCt/pPph+JMpmgCNv3KNe/qroBk+iF9O3huGTiIg91auB3eMkVXQB5BOJMBmg4BixF9OtaeL8Zf6AKeg3fJIRHTwcu7C1UHAxTZW90pCqodH5fvZ48KhfWVlu9tozmQsLDqV7V3rG+azx4jxi4s2M2NUK7mcahrLFdIoru8/cbj5o4syM2NVipRXtQEr7bTrFkXV6zAsd82P4iInsDH9CD3f6HaO0QWrhrG6mHFv++DnE2qGQ4zu4PM76nSO0d/3N8ZtScb41Nl2E69LIMvhYM7VhZZzZ83aWTwT5lxxx0ZnFUrQNI38dFFxGP2Rn6WQZezFzBTpk+A/WaSPs4XM5OmqNURtSBAYdXMqMuBhk5/ijONSKDowHw0xZhH7hM2ULDfBYJd+KxBoA5MOiqKMSmMO01fs2quQbiliDALAfc3e4FCBmYxLHZlM+yw9K2UvpQhPrYOzSS6i6tGwWXHW7C91819RVkv+LWIMh0Z/GCDMeMNb/OEZ6zWsnXtelpQ+/yEnmH72J6uacPgacAAAAAElFTkSuQmCC"
                                            />
                                            <Text style={{width: '100%', position: 'absolute', top: 2.5}}>
                                                {lowestAndClosestIndex.lowest + 1}
                                            </Text>
                                        </View>
                                    }
                                    <View style={{ width: '90%' }}>
                                        <Text style={{...styles.fontBold}}>LOWEST PER DAY (AUD)</Text>
                                        <Text style={[{...styles.fontBold, ...styles.fontXl}, {marginTop: 4}, {marginBottom: 4}]}>
                                            ${props.resultSummary.lowestPerDay.per_day_price}
                                        </Text>
                                        <Text style={{...styles.wFull}}>
                                            {props.resultSummary.lowestPerDay.vehicle_data.name} ({props.resultSummary.lowestPerDay.sipp_code})
                                        </Text>
                                        <Text style={[{...styles.wFull, ...styles.fontBold}, { marginTop: 11}]}>
                                            {props.resultSummary.lowestPerDay.supplier_name || getSupplierName(props.resultSummary.lowestPerDay.supplier_code)} ({props.resultSummary.lowestPerDay.depot_code})
                                        </Text>
                                        <Text style={{...styles.wFull}}>
                                            {props.resultSummary.lowestPerDay.depot_address}
                                        </Text>
                                    </View>
                                </View>
                            </View>
                            <View style={{...styles.wHalf, ...styles.fontXs}}>
                                <View style={{ ...styles.row}}>
                                    {
                                        typeof lowestAndClosestIndex.closest !== 'undefined' &&
                                        <View style={{...styles.depotSummaryPinIconContainer}}>
                                            <Image allowDangerousPaths
                                                   style={{ maxWidth: 20}}
                                                   src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB4AAAAmCAYAAADTGStiAAAAAXNSR0IArs4c6QAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAHqADAAQAAAABAAAAJgAAAAB42OZ4AAADWElEQVRYCb1XS0hUYRQ+59ehXLiwZq4RLVpERNmiReRMtWoh0WPRy1pEbSIinfFBgUFoUVAbTYsKWpT0WETQwkqEKAKZa0lZURGhRbWIZgwt02ScuadzRu44D+84zsz1X9z//Ofxffd/nP+BkEEZueLRxkJQgUCbALCMAFxA5IqGIgRY94Pbb9j+1EHjXSW1r4dngsV0DoE29xoibGTA7Qye1jeGQxBm+RYYdFar0/tj+iRhWrDg+fXFNI+uAUJlkv+smgR0VYtAHdbp/5IDU4gHL7pXGBHo4A4uS3bOps3kXwoIK501/t74+ATiX81rV0WUQ+eeFsc75SoT0Sgq2Kh59T4TK0Y81FK+NITYg4ilpjGfNREMAk2sK63t/Sy4Sj6sxJBSHXaRCgcvTSeowpsiS4kSB9vKq7jrZZMq+74I6Am0lB8UhigxkDptH10iMqE6KRr8ecGzk4fhXqLZ3hYZxgbFm0NOuZrNL6LC3YrX1cpsgnOJ4d1wteKc1XIBySYWkZbzUPOGP+cFl0yu6jknlnQimPEIs+G/BhQhfbIBOD0k0SuFBC/Se9lgRXjG6QQPbIC2hiQyinD8rtIW64/Za8jaM88WxM5ib19Q4R6I8AKLnRp5pkmB45Pwuiij6eQohGYml7uSvYWoXxv23xeSKHFJlf8rn8rt9rJGz+RGbAIjRixCkRpv4F6P2EXOi/idy6ffMfFjO5dMOCBFz0rTmM+6AKgmHo+36qkiVyC+S3fLTWFKm7sk19xSn34kHinWY1HyhYAKw+G9eR7yAW1+qD6eVOQEYlEsrO/9zlfRfSLnWrinY0rRVjz8ciwZK4VYHFxe/0Mg40yy86zbSPud1frH6eIS5jjeQeY72Oru5PGviNdnLtM5zac3WPlP22NxlvkGx59dPFwfrIIt9URdLq9+wtLOBktiCdKOvv/rgILNLGa8l8uPGhOjcnPl1LUuaYklbIGv+xtAhJ+pNGENY1roNxjhbYuOvR01NVb1jMQSqPmedzPxISsQU68M3GG+jUydVZ0RsQRrvp52fmRVWwBFeFS2OGv9TyzsKeqMiSVSq9EvcRo0JaMgGgd4VB4l69O1Z0UsQC6f/xTn+GUTlN++x13enttm2/Y60Oq+EWj1ZL3J/AdyXwjk/xBV9wAAAABJRU5ErkJggg=="
                                            />
                                            <Text style={{width: '100%', position: 'absolute', top: 2.5}}>
                                                {lowestAndClosestIndex.closest + 1}
                                            </Text>
                                        </View>
                                    }
                                    <View style={{ width: '90%' }}>
                                        <Text style={{...styles.fontBold}}>PER DAY CLOSEST (AUD)</Text>
                                        <Text style={[{...styles.fontBold, ...styles.fontXl}, {marginTop: 4}, {marginBottom: 4}]}>
                                            ${props.resultSummary.closest.per_day_price}
                                        </Text>
                                        <Text style={{...styles.wFull}}>
                                            {props.resultSummary.closest.vehicle_data.name} ({props.resultSummary.closest.sipp_code})
                                        </Text>
                                        <Text style={[{...styles.wFull, ...styles.fontBold}, { marginTop: 11}]}>
                                            {props.resultSummary.closest.supplier_name || getSupplierName(props.resultSummary.closest.supplier_code)} ({props.resultSummary.closest.depot_code})
                                        </Text>
                                        <Text style={{...styles.wFull}}>
                                            {props.resultSummary.closest.depot_address}
                                        </Text>
                                    </View>
                                </View>
                            </View>
                        </View>
                    </View>
                )
            }
            <View style={[{...styles.row, ...styles.tableHeader}, {width: '100%'}]}>
                <View style={{...styles.depotSummaryTableCol, ...styles.depotSummaryTableColIdx}} />
                <View style={[{...styles.depotSummaryTableCol, ...styles.depotSummaryTableColDistance}]}>
                    <View style={{...styles.depotSummaryTableColContainer}}>
                        <Text style={{...styles.fontBold}}>
                            DISTANCE FROM CENTER
                        </Text>
                    </View>
                </View>
                <View style={{...styles.depotSummaryTableCol, ...styles.depotSummaryTableColPrice}}>
                    <View style={{...styles.depotSummaryTableColContainer}}>
                        <Text style={{...styles.fontBold}}>
                            LOWEST PER DAY (AUD)
                        </Text>
                    </View>
                </View>
                <View style={{...styles.depotSummaryTableCol, ...styles.depotSummaryTableColPrice}}>
                    <View style={{...styles.depotSummaryTableColContainer}}>
                        <Text style={{...styles.fontBold}}>
                            HIGHEST PER DAY (AUD)
                        </Text>
                    </View>
                </View>
                <View style={{...styles.depotSummaryTableCol, ...styles.depotSummaryTableColSupplier}}>
                    <View style={{...styles.depotSummaryTableColContainer}}>
                        <Text style={{...styles.fontBold}}>
                            SUPPLIER
                        </Text>
                    </View>
                </View>
                <View style={{...styles.depotSummaryTableCol, ...styles.depotSummaryTableColAddress}}>
                    <View style={{...styles.depotSummaryTableColContainer}}>
                        <Text style={{...styles.fontBold}}>
                            DEPOT
                        </Text>
                    </View>
                </View>
            </View>
            {
                props.resultSummary.depotSummary.map((depot, index) => (
                    <View wrap={false}
                          key={index}
                          style={[{...styles.row}, {width: '100%'}, {backgroundColor: index % 2 === 0 ? '#ffffff' : '#F5F6FA'}]}
                    >
                        <View style={{...styles.depotSummaryTableCol, ...styles.depotSummaryTableColIdx}}>
                            <View style={{...styles.depotSummaryTableColContainer}}>
                                <View style={[
                                    {...styles.depotSummaryIndexNumber},
                                    { backgroundColor: getBackgroundColor(index, depot)}
                                ]}>
                                    <Text style={{...styles.fontXs}}>
                                        {(index+1).toString()}
                                    </Text>
                                </View>
                            </View>
                        </View>
                        <View style={{...styles.depotSummaryTableCol, ...styles.depotSummaryTableColDistance}}>
                            <View style={{...styles.depotSummaryTableColContainer}}>
                                <Text style={{...styles.fontXs}}>
                                    {depot.distance.toFixed(2)}km
                                </Text>
                            </View>
                        </View>
                        <View style={{...styles.depotSummaryTableCol, ...styles.depotSummaryTableColPrice}}>
                            <View style={{...styles.depotSummaryTableColContainer}}>
                                {
                                    depot.lowestPerDay !== null &&
                                    <View>
                                        <Text style={{...styles.fontBold, ...styles.fontXs, ...styles.wFull}}>
                                            ${depot.lowestPerDay.per_day_price}
                                        </Text>
                                        <Text style={{...styles.fontXs}}>
                                            {depot.lowestPerDay.duration} day{depot.lowestPerDay.duration > 1 ? 's' : ''}
                                    </Text>
                                    </View>
                                }
                            </View>
                        </View>
                        <View style={{...styles.depotSummaryTableCol, ...styles.depotSummaryTableColPrice}}>
                            <View style={{...styles.depotSummaryTableColContainer}}>
                                {
                                    depot.highestPerDay !== null &&
                                    <View>
                                        <Text style={{...styles.fontBold, ...styles.fontXs}}>
                                            {depot.highestPerDay.per_day_price}
                                        </Text>
                                        <Text style={{...styles.fontXs}}>
                                            {depot.highestPerDay.duration} day{depot.highestPerDay.duration > 1 ? 's' : ''}
                                        </Text>
                                    </View>
                                }
                            </View>
                        </View>
                        <View style={{...styles.depotSummaryTableCol, ...styles.depotSummaryTableColSupplier}}>
                            <View style={{...styles.depotSummaryTableColContainer}}>
                                <Text style={{...styles.fontXs}}>
                                    {depot.supplier_name || getSupplierName(depot.supplier_code)}
                                </Text>
                            </View>
                        </View>
                        <View style={{...styles.depotSummaryTableCol, ...styles.depotSummaryTableColAddress}}>
                            <View style={{...styles.depotSummaryTableColContainer}}>
                                <Text style={{...styles.fontBold, ...styles.fontXs}}>
                                    {depot.depot_code}
                                </Text>
                                <Text style={{...styles.wFull, ...styles.fontXs}}>
                                    {getDepotAddress(props.depotsAtArea, `${depot.depot_code}_${depot.supplier_code}`)}
                                </Text>
                            </View>
                        </View>
                    </View>
                ))
            }
            {
                props.activeReportFilters.indexOf(ActiveReportFilters.DEPOTS) < 0 &&
                props.depotsWithNoResults.map((depot, index) => (
                    <View wrap={false}
                          key={index}
                          style={[{...styles.row}, {width: '100%'}, {backgroundColor: index % 2 === 0 ? '#ffffff' : '#F5F6FA'}]}
                    >
                        <View style={{...styles.depotSummaryTableCol, ...styles.depotSummaryTableColIdx}}>
                            <View style={{...styles.depotSummaryTableColContainer}}>
                                <View style={[
                                    {...styles.depotSummaryIndexNumber},
                                    {backgroundColor: '#898989'}
                                ]}>
                                    <Text style={{...styles.fontXs}}>
                                        {((index+1) + props.resultSummary.depotSummary.length).toString()}
                                    </Text>
                                </View>
                            </View>
                        </View>
                        <View style={{...styles.depotSummaryTableCol, ...styles.depotSummaryTableColDistance}}>
                            <View style={{...styles.depotSummaryTableColContainer}}>
                                <Text style={{...styles.fontXs}}>
                                    {depot.distance.toFixed(2)}km
                                </Text>
                            </View>
                        </View>
                        <View style={{...styles.depotSummaryTableCol, ...styles.depotSummaryTableColPrice}}>
                            <View style={{...styles.depotSummaryTableColContainer}}>
                                {
                                    <Text style={{...styles.fontXs}}>
                                       No rates available
                                    </Text>
                                }
                            </View>
                        </View>
                        <View style={{...styles.depotSummaryTableCol, ...styles.depotSummaryTableColPrice}}>
                            <View style={{...styles.depotSummaryTableColContainer}}>
                                <Text style={{...styles.fontBold, ...styles.fontXs}} />
                            </View>
                        </View>
                        <View style={{...styles.depotSummaryTableCol, ...styles.depotSummaryTableColSupplier}}>
                            <View style={{...styles.depotSummaryTableColContainer}}>
                                <Text style={{...styles.fontXs}}>
                                    {depot.supplier_name || getSupplierName(depot.supplier_code)}
                                </Text>
                            </View>
                        </View>
                        <View style={{...styles.depotSummaryTableCol, ...styles.depotSummaryTableColAddress}}>
                            <View style={{...styles.depotSummaryTableColContainer}}>
                                <Text style={{...styles.fontBold, ...styles.fontXs}}>
                                    {depot.depot_code}
                                </Text>
                                <Text style={{...styles.wFull, ...styles.fontXs}}>
                                    {depot.address}
                                </Text>
                            </View>
                        </View>
                    </View>
                ))
            }
            <View>
                <Text style={styles.copyrightText}>
                    {props.resultSummary.depotSummary.length}
                    {' '}out of {totalDepots}
                    {' '}depots.
                </Text>
            </View>
        </View>
    )
};

export default DepotSummary;
